import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Copy, CheckCircle2, AlertTriangle, RefreshCw, Wand2 } from 'lucide-react';
import { Button, Badge, TextArea, Input, FormField, Select } from '@/components/ui';
import { copyToClipboard } from '@/utils/clipboard';
import {
  generateAlbumCoverConcepts,
  type AlbumCoverConcept,
} from '@/services/albumCoverConceptsService';
import { MODIFIER_TOOLTIPS } from '@/data/cinematicModifiers';
import { parseSongMarkdown } from '@/utils/parseSongMarkdown';
import { ASPECT_RATIO_OPTIONS } from '@/components/PromptArchitect/constants';

interface ExperimentalTabProps {
  /** Same markdown blob the Prompt Architect sees — auto-parsed to seed fields. */
  masterInput: string;
}

const ExperimentalTab: React.FC<ExperimentalTabProps> = ({ masterInput }) => {
  const parsed = useMemo(() => parseSongMarkdown(masterInput), [masterInput]);

  const [title, setTitle] = useState(parsed.title);
  const [artist, setArtist] = useState(parsed.artist);
  const [summary, setSummary] = useState(parsed.summary);
  const [lyrics, setLyrics] = useState(parsed.lyrics);
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [concepts, setConcepts] = useState<AlbumCoverConcept[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  // Re-seed fields if the parent's masterInput swaps (e.g., new song sent to Art)
  useEffect(() => {
    setTitle(parsed.title);
    setArtist(parsed.artist);
    setSummary(parsed.summary);
    setLyrics(parsed.lyrics);
  }, [parsed.title, parsed.artist, parsed.summary, parsed.lyrics]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await generateAlbumCoverConcepts({ title, artist, summary, lyrics, aspectRatio });
      if (result.length === 0) {
        setError('The model returned no concepts. Try again or check your API key.');
      }
      setConcepts(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Concept generation failed.';
      setError(msg);
      setConcepts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (idx: number, concept: AlbumCoverConcept) => {
    const modifierLines = Object.entries(concept.modifiers)
      .filter(([, opts]) => opts && opts.length > 0)
      .map(([cat, opts]) => `${cat}: ${opts.join(', ')}`)
      .join('\n');
    const payload = modifierLines
      ? `${concept.text}\n\n--- MODIFIERS ---\n${modifierLines}`
      : concept.text;
    await copyToClipboard(payload);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1800);
  };

  const canGenerate = title.trim().length > 0 || lyrics.trim().length > 20;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-10 h-10 rounded-xl bg-accent text-slate-950 inline-flex items-center justify-center mb-3">
          <Wand2 size={20} />
        </div>
        <h2 className="text-2xl font-bold text-slate-50">
          Experimental <span className="text-accent">Concepts</span>
        </h2>
        <p className="text-sm text-slate-400 mt-2 max-w-xl mx-auto">
          Three distinct album cover directions generated from song context. Each concept comes
          with LLM-picked cinematic modifiers.
        </p>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField label="Song Title">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Blacktop Prayer"
          />
        </FormField>
        <FormField label="Artist / Band">
          <Input
            value={artist}
            onChange={(e) => setArtist(e.target.value)}
            placeholder="Leave blank to let the model invent one"
          />
        </FormField>
        <FormField label="Aspect Ratio">
          <Select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            options={ASPECT_RATIO_OPTIONS}
          />
        </FormField>
      </div>
      <FormField label="Summary / Style">
        <TextArea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="Genre + vibe. Short paragraph is fine."
          rows={3}
        />
      </FormField>
      <FormField label="Lyrics">
        <TextArea
          value={lyrics}
          onChange={(e) => setLyrics(e.target.value)}
          placeholder="Paste the lyrics (with or without section tags)."
          rows={8}
        />
      </FormField>

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={handleGenerate}
          disabled={loading || !canGenerate}
          variant="primary"
          size="lg"
        >
          {loading ? (
            <>
              <RefreshCw size={18} className="animate-spin mr-2" />
              Generating 3 concepts…
            </>
          ) : (
            <>
              <Sparkles size={18} className="mr-2" />
              Generate 3 Concepts
            </>
          )}
        </Button>
        {!canGenerate && (
          <span className="text-xs text-slate-500">
            Add a title or paste lyrics to enable generation.
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-800/50 bg-red-950/30 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-300">{error}</div>
        </div>
      )}

      {/* Concepts */}
      {concepts.length > 0 && (
        <div className="space-y-4">
          {concepts.map((concept, idx) => (
            <div
              key={idx}
              className="rounded-xl border border-slate-800 bg-slate-950/40 backdrop-blur-sm overflow-hidden"
            >
              <div className="border-l-4 border-l-accent p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-black uppercase tracking-widest text-accent">
                    Concept {idx + 1}
                  </h3>
                  <Button size="sm" variant="ghost" onClick={() => handleCopy(idx, concept)}>
                    {copiedIdx === idx ? (
                      <>
                        <CheckCircle2 size={14} className="mr-1.5" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy size={14} className="mr-1.5" /> Copy
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                  {concept.text}
                </p>
                {/* Modifier chips */}
                {Object.entries(concept.modifiers).some(([, opts]) => opts && opts.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-slate-800/60 space-y-2">
                    {Object.entries(concept.modifiers)
                      .filter(([, opts]) => opts && opts.length > 0)
                      .map(([category, opts]) => (
                        <div key={category} className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 min-w-[120px]">
                            {category}
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {opts.map((opt) => (
                              <Badge
                                key={opt}
                                variant="outline"
                                title={MODIFIER_TOOLTIPS[opt] ?? ''}
                              >
                                {opt}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExperimentalTab;
